<script setup lang="ts">
/**
 * H6b — Selected for you · Selections card carousel + persona subtitle
 * Figma RedisignHomePage `15:9720` / compact `15:9569`
 * ≠ ProductCardV2 — editorial FairSelectionsCardV2
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  fairScrollLeftTo,
  useFairDragScroll,
} from '@/composables/useFairDragScroll'
import { useAuthModal } from '@/features/auth/store'
import { useTasteClarificationModal } from '@/features/taste/store'
import { useRecommendationsBannersQuery } from '@/queries/recommendations'
import { PROFILE_ROUTE } from '@/router/routes'
import { FairSelectionsCardV2 } from '@/ui/patterns/FairSelectionsCardV2'
import { FairLink } from '@/ui/primitives/FairLink'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairIconContainer } from '@/ui/primitives/FairIconContainer'
import { FairPaginationBar } from '@/ui/primitives/FairPaginationBar'
import { FairPageLoaderMark } from '@/ui/patterns/FairPageLoader'
import { FAIR_PAGE_LOADER_INLINE_SIZE } from '@/ui/tokens/fair-page-loader.data'
import {
  FAIR_HOME_V2_SELECTED_DEFAULT_PERSONA,
  getFairHomeV2SelectedCardWidth,
  getFairHomeV2SelectedCardsPerView,
  getFairHomeV2SelectedGap,
  getFairHomeV2SelectedPageCount,
  mapFairHomeV2SelectedAudience,
  normalizeFairHomeV2SelectedPage,
  resolveFairHomeV2SelectedPersona,
  resolveFairHomeV2SelectedScrollPage,
  resolveFairHomeV2SelectedSlides,
  type FairHomeV2SelectedPersonaId,
  type FairHomeV2SelectedSlide,
} from './fair-home-v2-selected.data'
import './fair-home-v2-selected.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
    audience?: string
    persona?: FairHomeV2SelectedPersonaId
    viewportWidth?: number
  }>(),
  {
    compact: false,
    audience: 'women',
    persona: FAIR_HOME_V2_SELECTED_DEFAULT_PERSONA,
    viewportWidth: 1440,
  },
)

const { t } = useI18n()
const router = useRouter()
const authModal = useAuthModal()
const tasteStore = useTasteClarificationModal()

const page = ref(0)
const viewportRef = ref<HTMLElement | null>(null)

useFairDragScroll(viewportRef)

let rowScrollTarget: HTMLElement | null = null

const apiAudience = computed(() => mapFairHomeV2SelectedAudience(props.audience))

const bannersQuery = useRecommendationsBannersQuery(apiAudience)

const slides = computed(() =>
  resolveFairHomeV2SelectedSlides(bannersQuery.data.value?.data),
)

const isLoading = computed(
  () => bannersQuery.isLoading.value && bannersQuery.data.value == null,
)

const personaView = computed(() => resolveFairHomeV2SelectedPersona(props.persona))

const sectionTitle = computed(() => t(personaView.value.titleKey))

const sectionSubtitle = computed(() => t(personaView.value.subtitleKey))

const showSubtitleLink = computed(
  () => Boolean(personaView.value.linkKey) && props.persona !== 'profile-complete',
)

const subtitleLinkLabel = computed(() =>
  personaView.value.linkKey ? t(personaView.value.linkKey) : '',
)

const cardSize = computed(() => (props.compact ? 'mobile' : 'default'))

const cardWidth = computed(() => getFairHomeV2SelectedCardWidth(props.compact))

const cardGap = computed(() => getFairHomeV2SelectedGap(props.compact))

const cardsPerView = computed(() =>
  getFairHomeV2SelectedCardsPerView(props.compact, props.viewportWidth),
)

const pageCount = computed(() =>
  getFairHomeV2SelectedPageCount(slides.value.length, cardsPerView.value),
)

const safePage = computed(() =>
  normalizeFairHomeV2SelectedPage(page.value, pageCount.value),
)

const showNav = computed(
  () => !props.compact && slides.value.length > cardsPerView.value,
)

const canPrev = computed(() => safePage.value > 0)
const canNext = computed(() => safePage.value < pageCount.value - 1)

const showPager = computed(
  () => slides.value.length > 1 && pageCount.value > 1,
)

const sectionStyle = computed(() => ({
  '--fair-h2-selected-gap': `${cardGap.value}px`,
  '--fair-h2-selected-card-width': `${cardWidth.value}px`,
}))

const rootClass = computed(() => ({
  'fair-home-v2-selected': true,
  'fair-home-v2-selected--compact': props.compact,
  'fair-home-v2-selected--desktop': !props.compact,
}))

watch(
  () => slides.value.length,
  () => {
    page.value = normalizeFairHomeV2SelectedPage(page.value, pageCount.value)
    void nextTick(() => {
      scrollToPage(page.value, 'auto')
      syncScrollPage()
    })
  },
)

watch(
  () => props.compact,
  () => {
    page.value = 0
    void nextTick(() => {
      scrollToPage(0, 'auto')
    })
  },
)

watch(
  () => props.viewportWidth,
  () => {
    page.value = normalizeFairHomeV2SelectedPage(page.value, pageCount.value)
    void nextTick(() => {
      scrollToPage(page.value, 'auto')
    })
  },
)

watch(
  () => props.audience,
  () => {
    void bannersQuery.refresh(true)
  },
)

function scrollStep(): number {
  return cardWidth.value + cardGap.value
}

function scrollToPage(index: number, behavior: ScrollBehavior = 'smooth') {
  const el = viewportRef.value
  if (!el) return
  const step = scrollStep()
  if (step <= 0) return
  const next = normalizeFairHomeV2SelectedPage(index, pageCount.value)
  page.value = next
  const left = next * step
  if (behavior === 'auto') {
    el.scrollLeft = left
    return
  }
  fairScrollLeftTo(el, left, { durationMs: 720 })
}

function unbindRowScroll() {
  if (!rowScrollTarget) return
  rowScrollTarget.removeEventListener('scroll', handleScroll)
  rowScrollTarget = null
}

function bindRowScroll(el: HTMLElement | null) {
  unbindRowScroll()
  if (!el) return
  el.addEventListener('scroll', handleScroll, { passive: true })
  rowScrollTarget = el
  syncScrollPage()
}

function syncScrollPage() {
  const el = viewportRef.value
  if (!el) return
  const step = scrollStep()
  if (step <= 0) return
  page.value = resolveFairHomeV2SelectedScrollPage(
    el.scrollLeft,
    step,
    pageCount.value,
  )
}

function selectionTo(slide: FairHomeV2SelectedSlide) {
  return {
    name: 'recommendation-banners' as const,
    params: { bannerId: String(slide.id) },
  }
}

function handlePrev() {
  if (!canPrev.value) return
  scrollToPage(safePage.value - 1)
}

function handleNext() {
  if (!canNext.value) return
  scrollToPage(safePage.value + 1)
}

function handlePager(index: number) {
  scrollToPage(index)
}

function handleScroll() {
  syncScrollPage()
}

function handleCardClick(slide: FairHomeV2SelectedSlide) {
  void router.push(selectionTo(slide))
}

function handleSubtitleLinkClick() {
  if (props.persona === 'guest') {
    authModal.showAuthModal('signInSignUpWrapper')
    return
  }
  if (props.persona === 'newcomer') {
    void tasteStore.showTasteModal()
    return
  }
  void router.push(PROFILE_ROUTE)
}

watch(viewportRef, (el) => {
  bindRowScroll(el)
})

onMounted(() => {
  bindRowScroll(viewportRef.value)
})

onBeforeUnmount(() => {
  unbindRowScroll()
})
</script>

<template>
  <section
    :class="rootClass"
    :style="sectionStyle"
    aria-label="selected for you"
  >
    <header class="fair-home-v2-selected__heading">
      <div class="fair-home-v2-selected__heading-text">
        <h2 class="fair-home-v2-selected__title">
          {{ sectionTitle }}
        </h2>
        <div class="fair-home-v2-selected__subtitle-row">
          <div class="fair-home-v2-selected__subtitle-copy">
            <FairIconContainer
              v-if="personaView.showAiIcon"
              class="fair-home-v2-selected__subtitle-icon"
              name="ai"
              :size="12"
            />
            <p class="fair-home-v2-selected__subtitle">
              {{ sectionSubtitle }}
            </p>
          </div>
          <FairLink
            v-if="showSubtitleLink && !compact"
            class="fair-home-v2-selected__subtitle-link"
            type="static"
            :label="subtitleLinkLabel"
            @click="handleSubtitleLinkClick"
          />
          <button
            v-else-if="showSubtitleLink && compact"
            type="button"
            class="fair-home-v2-selected__improve-chip"
            @click="handleSubtitleLinkClick"
          >
            {{ subtitleLinkLabel }}
          </button>
        </div>
      </div>
      <div
        v-if="showNav"
        class="fair-home-v2-selected__nav"
      >
        <FairIconButton
          icon="arrow-back"
          size="small"
          :disabled="!canPrev"
          :aria-label="t('fair_home_v2.selected.carousel_prev')"
          @click="handlePrev"
        />
        <FairIconButton
          icon="arrow-forward"
          size="small"
          :disabled="!canNext"
          :aria-label="t('fair_home_v2.selected.carousel_next')"
          @click="handleNext"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="fair-home-v2-selected__loading"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <FairPageLoaderMark
        motion="draw"
        :size="FAIR_PAGE_LOADER_INLINE_SIZE"
      />
    </div>

    <div
      v-else-if="slides.length"
      ref="viewportRef"
      class="fair-home-v2-selected__row"
      @scroll.passive="handleScroll"
    >
      <FairSelectionsCardV2
        v-for="slide in slides"
        :key="slide.id"
        class="fair-home-v2-selected__card"
        :image-url="slide.imageUrl"
        :image-alt="slide.imageAlt ?? slide.title"
        :title="slide.title"
        :description="slide.description"
        :size="cardSize"
        :to="selectionTo(slide)"
        @click="handleCardClick(slide)"
      />
    </div>

    <p
      v-else
      class="fair-home-v2-selected__empty"
    >
      {{ t('fair_home_v2.selected.empty') }}
    </p>

    <div
      v-if="showPager"
      class="fair-home-v2-selected__pager"
    >
      <FairPaginationBar
        :count="pageCount"
        :model-value="safePage"
        @update:model-value="handlePager"
      />
    </div>
  </section>
</template>
